import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';

// Top progress bar (§17a): appears whenever the app is talking to the API, as a glowing accent
// line that eases toward the end and slips away when done.

export function TopProgress() {
  const busy = useIsFetching() + useIsMutating() > 0;
  return (
    <AnimatePresence>
      {busy && (
        <motion.div
          className="fixed inset-x-0 top-0 z-[60] h-[3px] origin-left"
          style={{
            background: 'linear-gradient(90deg, var(--primary), var(--accent), var(--glow-2))',
            boxShadow: '0 0 12px var(--accent)',
          }}
          initial={{ scaleX: 0, opacity: 1 }}
          animate={{ scaleX: 0.85, transition: { duration: 6, ease: [0.1, 0.8, 0.2, 1] } }}
          exit={{ scaleX: 1, opacity: 0, transition: { duration: 0.35 } }}
        />
      )}
    </AnimatePresence>
  );
}

/**
 * "Tide" loader for long operations (§17a): a wave rises in the card while status lines rotate.
 * `lines` come from i18n by the caller.
 */
export function Tide({ lines, label }: { lines: string[]; label: string }) {
  return (
    <div
      role="status"
      aria-label={label}
      className="relative h-28 overflow-hidden rounded-lg bg-subtle"
    >
      <motion.svg
        viewBox="0 0 400 60"
        preserveAspectRatio="none"
        className="absolute inset-x-0 bottom-0 h-full w-[200%]"
        initial={{ y: '70%' }}
        animate={{ y: ['70%', '25%', '45%'], x: ['0%', '-50%'] }}
        transition={{
          y: { duration: 6, repeat: Infinity, repeatType: 'mirror' },
          x: { duration: 4, repeat: Infinity, ease: 'linear' },
        }}
      >
        <path
          d="M0 30 Q 50 10 100 30 T 200 30 T 300 30 T 400 30 V 60 H 0 Z"
          fill="color-mix(in oklab, var(--primary) 35%, transparent)"
        />
      </motion.svg>
      <div className="relative grid h-full place-items-center px-4 text-center text-base font-semibold text-text">
        <RotatingLine lines={lines} />
      </div>
    </div>
  );
}

function RotatingLine({ lines }: { lines: string[] }) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (lines.length < 2) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % lines.length), 2200);
    return () => clearInterval(timer);
  }, [lines.length]);
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={index}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.3 }}
      >
        {lines[index]}
      </motion.span>
    </AnimatePresence>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={`skeleton ${className ?? ''}`} aria-hidden="true" />;
}
